import { useCallback, useMemo, useRef } from "react";

export const DEFAULT_RECORDING_CONTENT_TYPE = "audio/webm";
export const SUPPORTED_RECORDING_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/mpeg"
];

const isMediaRecorderStateActive = recorderState => {
  return recorderState === "recording" || recorderState === "paused";
};

// Hide STT when required browser APIs are missing instead of exposing unusable controls.
export const isBrowserAudioRecorderSupported = () => {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
};

// Browser API boundary: manages MediaRecorder/getUserMedia lifecycle and chunk buffers.
const useBrowserAudioRecorder = () => {
  // We keep recorder internals in refs because MediaRecorder callbacks are imperative
  // and should not trigger React re-renders on each chunk/event.
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingHandledRef = useRef(false);
  const discardNextRecordingRef = useRef(false);

  const isMediaRecorderSupported = useMemo(() => {
    return isBrowserAudioRecorderSupported();
  }, []);

  const isMediaRecorderActive = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    return recorder && isMediaRecorderStateActive(recorder.state);
  }, []);

  const stopMediaStreamTracks = useCallback(() => {
    const stream = mediaStreamRef.current;
    mediaStreamRef.current = null;

    if (!stream) return;

    // Explicitly stop all tracks to turn off microphone usage indicator immediately.
    stream.getTracks().forEach(track => track.stop());
  }, []);

  const ensureMediaStream = useCallback(async () => {
    if (!isMediaRecorderSupported) return null;

    if (mediaStreamRef.current) return mediaStreamRef.current;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;

    return stream;
  }, [isMediaRecorderSupported]);

  const getSupportedRecordingMimeType = useCallback(() => {
    const mediaRecorderClass = window.MediaRecorder;
    if (!mediaRecorderClass?.isTypeSupported) return null;

    return SUPPORTED_RECORDING_MIME_TYPES.find(type =>
      mediaRecorderClass.isTypeSupported(type)
    );
  }, []);

  const createMediaRecorder = useCallback(async () => {
    const stream = await ensureMediaStream();
    if (!stream) return null;

    const mimeType = getSupportedRecordingMimeType();
    const recorderOptions = mimeType ? { mimeType } : undefined;
    const mediaRecorder = new MediaRecorder(stream, recorderOptions);

    mediaRecorderRef.current = mediaRecorder;

    return mediaRecorder;
  }, [ensureMediaStream, getSupportedRecordingMimeType]);

  const resetRecordingSession = useCallback(() => {
    recordingChunksRef.current = [];
    recordingHandledRef.current = false;
    discardNextRecordingRef.current = false;
  }, []);

  const appendRecordingChunk = useCallback(event => {
    if (event.data && event.data.size > 0) {
      recordingChunksRef.current = recordingChunksRef.current.concat([event.data]);
    }
  }, []);

  const consumeRecordedData = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    const recordingChunks = recordingChunksRef.current;

    // Clear mutable buffers as soon as data is consumed to avoid stale reuse.
    mediaRecorderRef.current = null;
    recordingChunksRef.current = [];

    return { mediaRecorder, recordingChunks };
  }, []);

  const setRecordingHandled = useCallback(nextValue => {
    recordingHandledRef.current = nextValue;
  }, []);

  const recordingHandled = useCallback(() => {
    return recordingHandledRef.current;
  }, []);

  const markDiscardNextRecording = useCallback(() => {
    discardNextRecordingRef.current = true;
  }, []);

  const shouldDiscardRecording = useCallback(() => {
    return discardNextRecordingRef.current;
  }, []);

  const clearDiscardRecordingFlag = useCallback(() => {
    discardNextRecordingRef.current = false;
  }, []);

  const clearMediaRecorder = useCallback(() => {
    mediaRecorderRef.current = null;
  }, []);

  const stopActiveMediaRecorder = useCallback(({ detachOnStop = false } = {}) => {
    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || !isMediaRecorderStateActive(mediaRecorder.state)) return;

    if (detachOnStop) {
      mediaRecorder.onstop = null;
    }

    mediaRecorder.stop();
  }, []);

  return {
    appendRecordingChunk,
    clearDiscardRecordingFlag,
    clearMediaRecorder,
    consumeRecordedData,
    createMediaRecorder,
    ensureMediaStream,
    isMediaRecorderActive,
    isMediaRecorderSupported,
    markDiscardNextRecording,
    mediaRecorderRef,
    mediaStreamRef,
    recordingHandled,
    resetRecordingSession,
    setRecordingHandled,
    shouldDiscardRecording,
    stopActiveMediaRecorder,
    stopMediaStreamTracks
  };
};

export default useBrowserAudioRecorder;
